// Build the ZAMBA ordering-code string from a spec + selected options.
//
// Code format (per spec):
//   Base: "<basePrefix> <length><digit><digit>…(RAL)<digit>"
//   Accessory: "<accessoryPrefix><digit><digit>…(MERV)<digit>"
//   Full: "<base>  <accessory>"  (two spaces separator)
//
// Special encodings:
//   • RAL field: when value === 3, emit "3(xxxx)" where xxxx is the RAL ral_xxxx
//     stored alongside the option (key + "_ral"). Otherwise emit the digit.
//   • MERV field: emits the 2-digit code wrapped in "(xx)" inline.
//   • Length === "X" → emits "X" (model C 10ft).
//
// Auto fields (field.auto === true):
//   • no_coil_pipes  — read from opts.project?.advanced?.no_coil_pipes ?? 2
//                      (project-level pipe count set in Selection Criteria tab)
//   • coil_circuits  — read from opts.circuits (the engine-selected circuit count
//                      carried on the group object from buildUnitSchedule)
//   For all other auto fields the value already stored in base[] is used as-is
//   (nozzle_type and num_nozzles are pre-seeded by the Configurator sync effect).

function encodeField(field, value, extras) {
  if (value === undefined || value === null || value === "") {
    value = field.default;
  }
  if (field.type === "ral") {
    const ral = extras?.[field.key + "_ral"] || "9075"; // default RAL on custom
    if (value === 3) return `3(${ral})`;
    return String(value);
  }
  if (field.type === "merv") {
    // Stored as 2-digit string already
    return `(${value})`;
  }
  if (field.type === "nozzle_count") {
    // Numeric nozzle count wrapped in parens, e.g. "(34)" or "(134)".
    if (value === null || value === undefined || value === "") return "(--)";
    return `(${value})`;
  }
  return String(value);
}

export function buildBaseCode(spec, base, extras, opts = {}) {
  if (!spec) return "";
  const unitModel = opts.unitModel || null;
  // no_coil_pipes is a project-level setting; coil_circuits comes from the
  // engine-selected circuit count on the group (both tagged auto: true).
  const noCoilPipes = opts.project?.advanced?.no_coil_pipes ?? 2;
  const circuits = opts.circuits ?? null;
  const parts = spec.baseFields
    .filter((f) => {
      // Field-level relevance for shared catalogs.
      // The Model O-1 / O-2 spec includes `coil_connection`; it must NOT
      // be encoded in the printed code for Model O-1 rooms.
      if (f.key === "coil_connection" && unitModel === "Model O-1") return false;
      return true;
    })
    .map((f) => {
      // Resolve auto fields that are sourced outside base[]:
      if (f.auto && f.key === "no_coil_pipes") return encodeField(f, noCoilPipes, extras);
      if (f.auto && f.key === "coil_circuits" && circuits != null) return encodeField(f, circuits, extras);
      return encodeField(f, base?.[f.key], extras);
    });
  // Use the room's actual unit_model as the prefix so a single shared spec
  // (e.g. S / S-HW) prints the correct model identifier in the code.
  const prefix = unitModel
    ? `B-${unitModel.replace(/^Model\s+/i, "")}`
    : spec.basePrefix;
  return `${prefix}${parts.join("")}`;
}

export function buildAccessoryCode(spec, accessory, extras) {
  if (!spec) return "";
  const parts = spec.accessoryFields.map((f) => encodeField(f, accessory?.[f.key], extras));
  return `${spec.accessoryPrefix}${parts.join("")}`;
}

export function buildFullCode(spec, config) {
  if (!spec) return "";
  const base = buildBaseCode(spec, config.base, config.extras, {
    unitModel: config.unit_model || config.unitModel,
    project: config.project,
    circuits: config.circuits,
  });
  const acc = buildAccessoryCode(spec, config.accessory, config.extras);
  return `${base}  ${acc}`;
}
